import * as Sentry from "@sentry/angular";
import { Injectable, inject } from "@angular/core";
import { LoadedEvent, TranslocoService } from "@jsverse/transloco";
import { toCamelCase } from "@jsverse/utils";

// Reports translation health to Sentry: keys requested but missing, and scopes whose
// translation file failed to load. Kept separate from ObservabilityService (the generic
// Sentry lifecycle facade) because tracking Transloco scope state is its own concern.
@Injectable({
    providedIn: "root",
})
export class I18nObservabilityService {
    private readonly transloco = inject(TranslocoService);

    // Scopes whose translation files Transloco has already loaded (from events$), keyed by
    // alias: the key prefix the missing handler sees, not the scope path. Used to tell a
    // real typo (scope loaded) from the load race (scope not loaded yet).
    private readonly loadedScopes = new Set<string>();
    private started = false;

    // Called from an APP_INITIALIZER so the subscription is in place from bootstrap. The
    // report* methods no-op until Sentry is init'd (ObservabilityService.initGlobal).
    startMonitoring(): void {
        if (this.started) return;
        this.started = true;

        this.transloco.events$.subscribe((event) => {
            if (event.type === "translationLoadSuccess") {
                this.loadedScopes.add(this.aliasOf(event.payload.scope ?? ""));
            } else if (event.type === "translationLoadFailure") {
                this.reportScopeLoadFailure(event.payload);
            }
        });
    }

    // Whether the scope with this alias (the key prefix) has loaded. The root/shared
    // namespace (empty scope) loads at bootstrap, so treat it as always loaded.
    isScopeLoaded(scope: string): boolean {
        return scope === "" || this.loadedScopes.has(scope);
    }

    // A translation key was requested but not found. scopeLoaded true → a real typo
    // (warning); false → the key raced the scope load (info). A pipe re-renders once the
    // scope loads, but an imperative translate() result stored before then stays a raw key.
    reportMissingKey(key: string, scope: string, scopeLoaded: boolean): void {
        Sentry.captureMessage(`i18n missing key: ${key}`, {
            level: scopeLoaded ? "warning" : "info",
            fingerprint: ["i18n-missing", key],
            tags: { i18nKey: key, i18nScope: scope },
        });
    }

    // Code called the sync t() of the Translation scope module before awaiting its ready(),
    // so the user sees the raw key. Production only; development throws instead.
    reportTranslateBeforeReady(key: string, scope: string): void {
        Sentry.captureMessage(`i18n translate before ready: ${key}`, {
            level: "error",
            fingerprint: ["i18n-translate-before-ready", key],
            tags: { i18nKey: key, i18nScope: scope },
        });
    }

    // The prefix Transloco stores a loaded scope's keys under: the camel-cased scope path
    // (Transloco's default alias; ADR-015 allows no other).
    private aliasOf(scope: string): string {
        return toCamelCase(scope);
    }

    // A scope's translation file failed to load (wrong scope path, missing loader depth
    // case, 404, network drop).
    reportScopeLoadFailure(payload: LoadedEvent["payload"]): void {
        const scope = payload.scope ?? "(root)";
        Sentry.captureMessage(`i18n scope load failure: ${scope}`, {
            level: "error",
            fingerprint: ["i18n-scope-load-failure", scope],
            tags: { i18nScope: scope, i18nLang: payload.langName },
        });
    }
}
